import { openDB as idbOpenDB } from 'idb';

const DB_NAME = 'clusterden_cache';
// Bump this number whenever stored data shapes change — IndexedDB will wipe old stores automatically.
const DB_VERSION = 2;

export const STORES = {
  INBOX_LISTS: 'inbox_lists',
  MESSAGES:    'messages',
  TEAM_DATA:   'team_data',
  LABELS:      'labels',
  CONTACTS:    'contacts',
};

// TTLs in milliseconds — owned by the frontend, not the backend.
export const TTL = {
  INBOX_LISTS: 5  * 60 * 1000,           // 5 minutes
  MESSAGES:    30 * 60 * 1000,           // 30 minutes
  TEAM_DATA:   24 * 60 * 60 * 1000,      // 24 hours
  LABELS:       7 * 24 * 60 * 60 * 1000, // 7 days
  CONTACTS:    60 * 60 * 1000,            // 1 hour
};

// Each store uses a different field as its IDB keyPath.
// This map drives setCached so the record shape is always correct.
const KEY_PATHS = {
  [STORES.INBOX_LISTS]: 'key',
  [STORES.MESSAGES]:    'contactId',
  [STORES.TEAM_DATA]:   'type',
  [STORES.LABELS]:      'id',
  [STORES.CONTACTS]:    'contactId',
};

let dbPromise = null;

const getDB = () => {
  if (!dbPromise) {
    dbPromise = idbOpenDB(DB_NAME, DB_VERSION, {
      upgrade(db) {
        // inbox_lists — keyed by a logical frontend name (e.g. 'snapshot')
        if (!db.objectStoreNames.contains(STORES.INBOX_LISTS)) {
          const s = db.createObjectStore(STORES.INBOX_LISTS, { keyPath: 'key' });
          s.createIndex('cachedAt', 'cachedAt');
        }

        // messages — one record per contactId
        if (!db.objectStoreNames.contains(STORES.MESSAGES)) {
          const s = db.createObjectStore(STORES.MESSAGES, { keyPath: 'contactId' });
          s.createIndex('cachedAt', 'cachedAt');
        }

        // team_data — keyed by type: 'teamMembers' | 'teams'
        if (!db.objectStoreNames.contains(STORES.TEAM_DATA)) {
          db.createObjectStore(STORES.TEAM_DATA, { keyPath: 'type' });
        }

        // labels — single record keyed by 'all'
        if (!db.objectStoreNames.contains(STORES.LABELS)) {
          db.createObjectStore(STORES.LABELS, { keyPath: 'id' });
        }

        // contacts — one record per contactId, 1-hour TTL
        if (!db.objectStoreNames.contains(STORES.CONTACTS)) {
          const s = db.createObjectStore(STORES.CONTACTS, { keyPath: 'contactId' });
          s.createIndex('cachedAt', 'cachedAt');
        }
      },
    });
  }
  return dbPromise;
};

/**
 * Returns cached data if the record exists and has not expired.
 * Deletes expired records on read so storage stays lean.
 * Returns null on any failure — the app must never crash over cache misses.
 */
export const getCached = async (storeName, key) => {
  try {
    const db = await getDB();
    const record = await db.get(storeName, key);
    if (!record) return null;
    if (Date.now() - record.cachedAt > record.ttl) {
      db.delete(storeName, key); // fire-and-forget cleanup
      return null;
    }
    return record.data;
  } catch {
    return null;
  }
};

/**
 * Writes data into the given store under key with a TTL.
 * Always fire-and-forget from call sites — never await this in hot paths.
 */
export const setCached = async (storeName, key, data, ttl) => {
  try {
    const db = await getDB();
    const keyField = KEY_PATHS[storeName];
    const record = { [keyField]: key, data, cachedAt: Date.now(), ttl };
    await db.put(storeName, record);
  } catch {
    // Silent — never crash the app over a failed cache write.
  }
};

/**
 * Deletes a single record. Use after a mutation invalidates specific cached data.
 */
export const invalidate = async (storeName, key) => {
  try {
    const db = await getDB();
    await db.delete(storeName, key);
  } catch {
    // Silent
  }
};

/**
 * Wipes every record in a store. Use when a broad invalidation is needed
 * (e.g. user logs out, workspace changes).
 */
export const invalidateStore = async (storeName) => {
  try {
    const db = await getDB();
    await db.clear(storeName);
  } catch {
    // Silent
  }
};

/**
 * Wipes all caches across every store. Call on logout.
 */
export const invalidateAll = async () => {
  await Promise.all(Object.values(STORES).map(invalidateStore));
};
